import { getExtensionValue, MedplumClient, QueryTypes } from "@medplum/core";
import {
  Organization,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import {
  mergeOrganizationAccountFirst,
  resourceHasOrganizationLinkage,
  setAccountsBestEffort,
} from "@/utils/accounts";
import { joinReferenceString, setExtensionValue } from "@/utils/medplum";
import { isMedplumForbiddenError } from "@/utils/medplum-forbidden";
import { collectResourcesFromNextPages } from "@/utils/pagination";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";
import { readBrowserStoredOrganizationId } from "@/utils/read-browser-stored-organization-id";
import { captureExceptionWithoutPhi } from "@/utils/sentry";
import { getValueSetExpansionCodings } from "@/utils/value-set";

import { ProcedureCode } from "../types/fee-table";
import { EditFeesFormType } from "../zod-schemas/edit-fees";

const PROCEDURE_FEE_QUESTIONNAIRE_URL = "http://quiltedhealth.com/q/procedure-fee";

/**
 * Page size for procedure-fee searches. `next` pages are followed, so this bounds each
 * request rather than the result set.
 */
const PROCEDURE_FEE_PAGE_SIZE = 500;

/**
 * Reads the HTTP status off a Medplum client error, which carries it either directly or
 * under `response`.
 *
 * @param error The rejected value from a Medplum client call
 * @returns The status code, or undefined when the error carries none
 */
function getErrorStatus(error: unknown): number | undefined {
  const medplumError = error as { status?: number; response?: { status?: number } };
  return medplumError.status ?? medplumError.response?.status;
}

/**
 * Runs one procedure-fee QuestionnaireResponse search and follows every `next` page.
 *
 * The fee schedule spans more procedure codes than one search page holds, and each code
 * stores its fees in its own response, so reading a single page returns the fees a practice
 * saved most recently and leaves the rest looking unset.
 *
 * @param medplum Medplum client
 * @param query FHIR search parameters for the QuestionnaireResponse search
 * @returns Every matching response across all pages, in server sort order, empty when the
 *   search itself reports nothing to read
 * @throws When a `next` page read fails, so a caller never mistakes a short read for the full set
 */
async function searchAllProcedureFeePages(
  medplum: MedplumClient,
  query: QueryTypes,
): Promise<QuestionnaireResponse[]> {
  let firstPage;
  try {
    firstPage = await medplum.searchResources("QuestionnaireResponse", query);
  } catch (error) {
    // Only the opening search can report "there is nothing here" as a 404. Once it has
    // returned rows, a later page failing the same way means fees exist and went unread.
    if (getErrorStatus(error) === 404) {
      return [];
    }
    throw error;
  }

  const nextPages = await collectResourcesFromNextPages<QuestionnaireResponse>(
    medplum,
    firstPage.bundle,
    "QuestionnaireResponse",
    // A dropped page would read as a complete result and hide fees again, so fail loudly.
    { throwOnPageError: true },
  );

  return [...firstPage, ...nextPages];
}

/**
 * Sorts procedure codes numerically first, then alphanumerically
 * Numeric codes (e.g., "59400", "12345") are sorted as numbers
 * Alphanumeric codes (e.g., "A123", "B456") are sorted alphabetically
 * Numeric codes always come before alphanumeric codes
 */
export function sortProcedureCodes(procedures: ProcedureCode[]): ProcedureCode[] {
  const isNumeric = (code: string): boolean => {
    return /^\d+$/.test(code);
  };

  const numericCodes: ProcedureCode[] = [];
  const alphanumericCodes: ProcedureCode[] = [];

  // Separate codes into numeric and alphanumeric
  for (const procedure of procedures) {
    if (isNumeric(procedure.code)) {
      numericCodes.push(procedure);
    } else {
      alphanumericCodes.push(procedure);
    }
  }

  // Sort numeric codes as numbers
  numericCodes.sort((a, b) => {
    const numA = parseInt(a.code, 10);
    const numB = parseInt(b.code, 10);
    return numA - numB;
  });

  // Sort alphanumeric codes alphabetically
  alphanumericCodes.sort((a, b) => {
    return a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });
  });

  // Return numeric codes first, then alphanumeric
  return [...numericCodes, ...alphanumericCodes];
}

/**
 * Loads procedure codes from a ValueSet and maps them to ProcedureCode format.
 * Codes come back in the order their concepts appear in the CodeSystem, which is the
 * fee schedule placement order. Codes missing from the CodeSystem keep expansion order at the end.
 * @param medplum The Medplum client
 * @param valueSetUrl The URL of the ValueSet to expand
 * @returns Array of ProcedureCode objects with default values
 */
export async function loadProcedureCodesFromValueSet(
  medplum: MedplumClient,
  valueSetUrl: string,
): Promise<ProcedureCode[]> {
  try {
    const codings = await getValueSetExpansionCodings(medplum, { url: valueSetUrl });

    // Group codings by system and fetch CodeSystem definitions for each unique system
    const codeSystemDefinitions: Map<string, string> = new Map();
    const codeSystemPositions: Map<string, number> = new Map();
    const uniqueSystems = new Set(
      codings.map((coding) => coding.system).filter((system): system is string => !!system),
    );

    // Fetch CodeSystems for each unique system to get definitions
    await Promise.all(
      Array.from(uniqueSystems).map(async (codeSystemUrl) => {
        try {
          const codeSystem = await medplum.searchOne("CodeSystem", {
            url: codeSystemUrl,
            status: "active",
          });
          if (codeSystem?.concept) {
            codeSystem.concept.forEach((concept, position) => {
              if (!concept.code) {
                return;
              }
              // Use a composite key (system:code) to avoid collisions across systems
              const conceptKey = `${codeSystemUrl}:${concept.code}`;
              codeSystemPositions.set(conceptKey, position);
              if (concept.definition) {
                codeSystemDefinitions.set(conceptKey, concept.definition);
              }
            });
          }
        } catch (error) {
          // The table still loads, with names for Info box text and expansion order for placement,
          // so report the failure instead of blocking the Fee Table.
          captureExceptionWithoutPhi(
            "Fee schedule CodeSystem read failed; showing names and expansion order",
            error,
            { feature: "fee-table", codeSystemUrl, valueSetUrl },
            "warning",
          );
        }
      }),
    );

    const getConceptKey = (coding: ValueSetExpansionContains): string => {
      const code = coding.code ?? "";
      return coding.system ? `${coding.system}:${code}` : code;
    };
    // Within one CodeSystem, placed codes sort by position; unplaced codes follow in expansion order.
    const compareByPosition = (
      a: ValueSetExpansionContains,
      b: ValueSetExpansionContains,
    ): number => {
      const positionA = codeSystemPositions.get(getConceptKey(a));
      const positionB = codeSystemPositions.get(getConceptKey(b));
      if (positionA === undefined || positionB === undefined) {
        return Number(positionA === undefined) - Number(positionB === undefined);
      }
      return positionA - positionB;
    };
    // Positions only compare within a CodeSystem, so each system's codes stay together, in the
    // order the systems first appear in the expansion.
    const codingsBySystem = codings.reduce((groups, coding) => {
      const system = coding.system ?? "";
      groups.set(system, [...(groups.get(system) ?? []), coding]);
      return groups;
    }, new Map<string, ValueSetExpansionContains[]>());
    const orderedCodings = [...codingsBySystem.values()].flatMap((systemCodings) =>
      [...systemCodings].sort(compareByPosition),
    );

    return orderedCodings.map((coding: ValueSetExpansionContains): ProcedureCode => {
      const system = coding.system ?? "";
      const code = coding.code ?? "";
      const definitionKey = getConceptKey(coding);
      return {
        code,
        name: coding.display ?? "",
        information: codeSystemDefinitions.get(definitionKey) ?? coding.display ?? "",
        isActive: true,
        insuranceFee: null,
        selfPayFee: null,
        // Store the full Coding for saving to QuestionnaireResponse
        coding: {
          system,
          code,
          display: coding.display ?? "",
        },
      };
    });
  } catch (error) {
    console.error(`Error loading procedure codes from ValueSet ${valueSetUrl}:`, error);
    throw error;
  }
}

/**
 * Fetches the procedure fee Questionnaire
 */
export async function fetchProcedureFeeQuestionnaire(
  medplum: MedplumClient,
): Promise<Questionnaire | undefined> {
  const questionnaire = await medplum.searchOne("Questionnaire", {
    url: PROCEDURE_FEE_QUESTIONNAIRE_URL,
    status: "active",
  });

  return questionnaire;
}

async function searchProcedureFeeQuestionnaireResponses(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  organizationReference?: string,
  practitionerReference?: Reference<Practitioner>,
): Promise<QuestionnaireResponse[]> {
  const questionnaireRef = joinReferenceString("Questionnaire", questionnaire.id!);
  const pageParams = { _sort: "-_lastUpdated", _count: PROCEDURE_FEE_PAGE_SIZE };

  if (organizationReference) {
    const orgRef = { reference: organizationReference } as Reference<Organization>;

    const primaryQuery = {
      questionnaire: questionnaireRef,
      _compartment: organizationReference,
      "subject:missing": true,
      ...pageParams,
    };
    let searchResults = await searchAllProcedureFeePages(medplum, primaryQuery);

    // Legacy fallback: org-scoped QRs that still use subject (any practitioner in the practice).
    if (searchResults.length === 0) {
      const legacyOrgQuery = {
        questionnaire: questionnaireRef,
        _compartment: organizationReference,
        ...pageParams,
      };
      searchResults = await searchAllProcedureFeePages(medplum, legacyOrgQuery);
    }

    if (searchResults.length === 0) {
      const unscoped = await searchAllProcedureFeePages(medplum, {
        questionnaire: questionnaireRef,
        ...pageParams,
      });
      searchResults = unscoped.filter((qr) => resourceHasOrganizationLinkage(qr, orgRef));
    }

    return searchResults;
  }

  if (!practitionerReference?.reference) {
    return [];
  }

  const authorQuery = {
    questionnaire: questionnaireRef,
    author: practitionerReference.reference,
    ...pageParams,
  };
  let searchResults = await searchAllProcedureFeePages(medplum, authorQuery);

  // Legacy fallback: older responses were queried by subject.
  if (searchResults.length === 0) {
    const legacySubjectQuery = {
      questionnaire: questionnaireRef,
      subject: practitionerReference.reference,
      ...pageParams,
    };
    searchResults = await searchAllProcedureFeePages(medplum, legacySubjectQuery);
  }

  return searchResults;
}

/**
 * Fetches an existing QuestionnaireResponse for a specific procedure code
 * Note: FHIR search doesn't support searching nested answer values easily,
 * so we fetch all QuestionnaireResponses for this questionnaire and filter by code
 * Note: This function is side-effect-free. Callers should invalidate cache if needed.
 *
 * @param medplum Medplum client
 * @param procedureCode The procedure code whose saved response to look for
 * @param questionnaire The procedure fee Questionnaire the response answers
 * @param practitionerReference Practitioner to scope the search to, when there is no organization
 * @param organizationReference Practice Organization reference string to scope the search to
 * @returns The saved response for that code, or undefined when the practice has saved none
 * @throws When the read fails. `undefined` means "this code has no saved response", which is
 *   what tells `saveProcedureFeeQuestionnaireResponse` to create one. Returning it for a failed
 *   read would create a second response alongside the one that went unread.
 */
export async function fetchProcedureFeeQuestionnaireResponse(
  medplum: MedplumClient,
  procedureCode: string,
  questionnaire: Questionnaire,
  practitionerReference?: Reference<Practitioner>,
  organizationReference?: string,
): Promise<QuestionnaireResponse | undefined> {
  const searchResults = await searchProcedureFeeQuestionnaireResponses(
    medplum,
    questionnaire,
    organizationReference,
    practitionerReference,
  );

  // Find the QuestionnaireResponse that matches the procedure code
  for (const questionnaireResponse of searchResults) {
    if (!questionnaireResponse || questionnaireResponse.resourceType !== "QuestionnaireResponse") {
      continue;
    }

    // Extract code directly from QuestionnaireResponse items
    // Support both valueCoding (new format) and valueString (legacy format)
    const codeItem = questionnaireResponse.item?.find((item) => item.linkId === "code");
    const codeAnswer = codeItem?.answer?.[0];
    const code = codeAnswer?.valueCoding?.code ?? codeAnswer?.valueString;

    if (code === procedureCode) {
      return questionnaireResponse;
    }
  }

  return undefined;
}

/**
 * Fetches all QuestionnaireResponses for a questionnaire, grouped by procedure code
 * This is more efficient than calling fetchProcedureFeeQuestionnaireResponse multiple times
 * Note: This function is side-effect-free. Callers should invalidate cache if needed.
 *
 * @param medplum Medplum client
 * @param questionnaire The procedure fee Questionnaire the responses answer
 * @param practitionerReference Practitioner to scope the search to, when there is no organization
 * @param organizationReference Practice Organization reference string to scope the search to
 * @returns The most recent response per procedure code, empty when the practice has saved none
 * @throws When the read fails, so a caller never shows an unconfigured schedule for a failed read
 */
export async function fetchProcedureFeeResponsesByCode(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  practitionerReference?: Reference<Practitioner>,
  organizationReference?: string,
): Promise<Record<string, QuestionnaireResponse>> {
  const searchResults = await searchProcedureFeeQuestionnaireResponses(
    medplum,
    questionnaire,
    organizationReference,
    practitionerReference,
  );

  const byCode: Record<string, QuestionnaireResponse> = {};
  for (const questionnaireResponse of searchResults) {
    if (!questionnaireResponse || questionnaireResponse.resourceType !== "QuestionnaireResponse") {
      continue;
    }

    const codeItem = questionnaireResponse.item?.find((item) => item.linkId === "code");
    const codeAnswer = codeItem?.answer?.[0];
    // Support both valueCoding (new format) and valueString (legacy format)
    const code = codeAnswer?.valueCoding?.code ?? codeAnswer?.valueString;
    if (code && !byCode[code]) {
      // Use the first (most recent) response for each code
      byCode[code] = questionnaireResponse;
    }
  }

  return byCode;
}

/**
 * Parses a QuestionnaireResponse into ProcedureCode data
 */
export function parseProcedureFeeQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse | undefined,
  procedureCode: ProcedureCode,
): ProcedureCode {
  if (!questionnaireResponse || !questionnaireResponse.item) {
    return procedureCode;
  }

  // Verify the code matches before parsing - safety check
  const codeItem = questionnaireResponse.item?.find((item) => item.linkId === "code");
  const storedCodeAnswer = codeItem?.answer?.[0];
  // Support both valueCoding (new format) and valueString (legacy format)
  const storedCode = storedCodeAnswer?.valueCoding?.code ?? storedCodeAnswer?.valueString;

  // Only use this QuestionnaireResponse if the code matches
  if (storedCode !== procedureCode.code) {
    return procedureCode;
  }

  // Extract answers directly from QuestionnaireResponse items
  const findAnswer = (linkId: string) => {
    const item = questionnaireResponse.item?.find((i) => i.linkId === linkId);
    return item?.answer?.[0];
  };

  const insuranceFeeAnswer = findAnswer("insuranceFee");
  const selfPayFeeAnswer = findAnswer("selfPayFee");
  const statusAnswer = findAnswer("status");

  return {
    ...procedureCode,
    insuranceFee: insuranceFeeAnswer?.valueDecimal ?? null,
    selfPayFee: selfPayFeeAnswer?.valueDecimal ?? null,
    isActive: statusAnswer?.valueBoolean ?? true,
  };
}

/**
 * Applies version increment to a QuestionnaireResponse
 * Increments the named version extension if an existing response is provided
 */
function applyNamedVersionIncrement(
  existing: QuestionnaireResponse | undefined,
  next: QuestionnaireResponse,
): QuestionnaireResponse {
  if (!existing) return next;

  const currentVersion = getExtensionValue(
    existing,
    Extensions.QuestionnaireResponseNamedVersion,
  )?.toString();

  const nextVersion =
    currentVersion != null ? `v${parseInt(currentVersion.slice(1), 10) + 1 || 1}` : "v1";

  return {
    ...next,
    extension: setExtensionValue(
      next.extension ?? [],
      Extensions.QuestionnaireResponseNamedVersion,
      { valueString: nextVersion },
    ),
  };
}

/**
 * Creates or updates a QuestionnaireResponse for a procedure fee
 * Includes hidden fields (name, code, information) from the procedure metadata
 *
 * @param activeOrganizationId - Practice Organization logical id (`:practiceId` or explicit). When omitted, uses `ORGANIZATION_ID` from localStorage if set.
 */
export async function saveProcedureFeeQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  procedureCode: ProcedureCode,
  feeValues: EditFeesFormType,
  practitionerReference: Reference<Practitioner>,
  practiceId: string,
  existingQuestionnaireResponse?: QuestionnaireResponse,
  activeOrganizationId?: string,
): Promise<QuestionnaireResponse> {
  const resolvedOrganizationId =
    typeof activeOrganizationId === "string" && activeOrganizationId.length > 0
      ? activeOrganizationId
      : readBrowserStoredOrganizationId();

  const organizationReference = resolvedOrganizationId
    ? joinReferenceString("Organization", resolvedOrganizationId)
    : undefined;

  let resolvedExistingQuestionnaireResponse = existingQuestionnaireResponse;
  if (!resolvedExistingQuestionnaireResponse && organizationReference) {
    resolvedExistingQuestionnaireResponse = await fetchProcedureFeeQuestionnaireResponse(
      medplum,
      procedureCode.code,
      questionnaire,
      undefined,
      organizationReference,
    );
  }

  // Prepare form values including hidden fields from procedure metadata
  const formValues: Record<string, unknown> = {
    name: procedureCode.name,
    code: procedureCode.coding ?? {
      code: procedureCode.code,
      display: procedureCode.name,
    },
    information: procedureCode.information,
    insuranceFee: feeValues.insuranceFee,
    selfPayFee: feeValues.selfPayFee,
    status: feeValues.isActive,
  };

  // Use the existing QuestionnaireResponse or create a new one.
  // Care provider policy allows org-scoped settings QuestionnaireResponses only when subject is
  // missing, so this response is built without one.
  let questionnaireResponseData = getQuestionnaireResponseFromFormValues(
    questionnaire,
    formValues,
    undefined,
    practitionerReference,
    resolvedExistingQuestionnaireResponse ?? null,
  );

  questionnaireResponseData = {
    ...questionnaireResponseData,
    author: practitionerReference,
  };

  // Set status to completed for saved fee configurations
  questionnaireResponseData.status = "completed";

  // Apply version increment if updating existing response
  questionnaireResponseData = applyNamedVersionIncrement(
    resolvedExistingQuestionnaireResponse,
    questionnaireResponseData,
  );

  const organizationAccountRef = resolvedOrganizationId
    ? ({
        reference: joinReferenceString("Organization", resolvedOrganizationId),
      } as Reference<Organization>)
    : undefined;

  if (organizationAccountRef) {
    questionnaireResponseData = {
      ...questionnaireResponseData,
      meta: {
        ...questionnaireResponseData.meta,
        accounts: mergeOrganizationAccountFirst(
          (questionnaireResponseData.meta as { accounts?: Reference[] } | undefined)?.accounts,
          organizationAccountRef,
        ),
      } as QuestionnaireResponse["meta"],
    };
  }

  const saveQuestionnaireResponse = async (
    payload: QuestionnaireResponse,
  ): Promise<QuestionnaireResponse> => {
    if (resolvedExistingQuestionnaireResponse?.id) {
      return await medplum.updateResource({
        ...payload,
        id: resolvedExistingQuestionnaireResponse.id,
      });
    }
    return await medplum.createResource(payload);
  };

  const mergedAccounts = questionnaireResponseData.meta?.accounts;

  let questionnaireResponse: QuestionnaireResponse | undefined;
  try {
    questionnaireResponse = await saveQuestionnaireResponse(questionnaireResponseData);
  } catch (error) {
    // Non-admin providers can be blocked from writing organization account linkage directly on
    // create/update. Retry without `meta.accounts`, then apply accounts via the bot.
    if (!organizationAccountRef || !isMedplumForbiddenError(error)) {
      throw error;
    }

    const { meta: existingMeta, ...rest } = questionnaireResponseData;
    const retriedPayload: QuestionnaireResponse = {
      ...rest,
      subject: undefined,
      ...(existingMeta
        ? {
            meta: {
              ...existingMeta,
              accounts: undefined,
            },
          }
        : {}),
    };
    questionnaireResponse = await saveQuestionnaireResponse(retriedPayload);
  }

  // Avoid redundant $set-accounts when create/update already applied org linkage. A second
  // $set-accounts can normalize meta and drop client fields (e.g. meta.accounts) before downstream bots run.
  if (!organizationAccountRef) {
    await setAccountsBestEffort(medplum, questionnaireResponse, [], { extractFromPatient: true });
  } else if (!resourceHasOrganizationLinkage(questionnaireResponse, organizationAccountRef)) {
    await setAccountsBestEffort(
      medplum,
      questionnaireResponse,
      mergedAccounts?.length ? mergedAccounts : [organizationAccountRef],
      { extractFromPatient: false },
    );
  }

  return questionnaireResponse;
}
